import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import Box from '@mui/material/Box';
import StoreIcon from '@mui/icons-material/Store';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { goToMenu } from 'bento-ordering/cart/actions/orderSettings';
import ModalCloseButton from 'bento-ordering/base/ui_components/ModalCloseButton';
import HowItWorks from 'bento-ordering/group_ordering/components/HowItWorks';
import { fulfillmentOptions } from 'constants/fulfillmentOptions';
import {
  GROUP_ORDERING_DEADLINE_INPUT_FORMAT,
  GROUP_ORDERING_DEADLINE_OUTPUT_FORMAT
} from 'bento-ordering/base/constants/timeFormat';
import {
  getGroupJoinTitle,
  getGroupJoinCTALabel
} from 'bento-ordering/utils/groupOrder';
import { GROUP_ORDER_DINER_NAME_INPUT_MAX_LENGTH } from 'bento-ordering/group_ordering/constants/groupOrder';
import {
  StyledGroupOrderWrapper,
  TitleTypography,
  StyledInputName,
  StyledGroupOrderButton
} from 'bento-ordering/group_ordering/ui_components/GroupOrder';
import GroupOrderJoinDetail from 'bento-ordering/group_ordering/components/GroupOrderJoinDetail';
import GroupOrderErrorToast from 'bento-ordering/group_ordering/components/GroupOrderErrorToast';
import { useIsMobile } from 'styles/hooks/mediaQueries';
import { joinGroupOrder } from 'bento-ordering/group_ordering/actions';
import { selectCartGroupOrder } from 'bento-ordering/cart/selectors/index';

const GroupOrderJoin = ({ handleClose }) => {
  const [nameInput, setNameInput] = useState('');
  const dispatch = useDispatch();
  const {
    admin_name: adminName,
    ordering_deadline: orderingDeadline,
    per_person_spending_limit: perPersonSpendingLimit,
    slug
  } = useSelector(selectCartGroupOrder);
  const cartState = useSelector(state => state.cart);
  const { site_title: siteTitle } = useSelector(state => state.account);
  const isMobile = useIsMobile();
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const deliveryAddress =
    cartState.online_ordering_fulfillment_method === fulfillmentOptions.DELIVERY
      ? cartState.shipping_street_address_1
      : '';

  const isCTAButtonDisabled = !nameInput.length;

  const handleNameChange = event => {
    setNameInput(event.target.value);
  };

  const renderJoinDetails = () => {
    const joinDetails = [];

    const details = {
      siteTitle: {
        icon: StoreIcon,
        text: siteTitle
      }
    };

    if (perPersonSpendingLimit) {
      details.perPersonSpendingLimit = {
        icon: CreditCardIcon,
        text: `$${perPersonSpendingLimit} Spending limit per person`
      };
    }

    if (orderingDeadline) {
      const formattedDateTime = moment(
        orderingDeadline,
        GROUP_ORDERING_DEADLINE_INPUT_FORMAT
      ).format(GROUP_ORDERING_DEADLINE_OUTPUT_FORMAT);

      details.orderingDeadline = {
        icon: AlarmOnIcon,
        text: `Due by ${formattedDateTime}`
      };
    }

    if (deliveryAddress) {
      details.deliveryAddress = {
        icon: LocationOnIcon,
        text: `Delivered to ${deliveryAddress}`
      };
    }

    const detailsLength = Object.keys(details).length;
    Object.keys(details).forEach((key, index) => {
      const isLast = index === detailsLength - 1;
      const marginBottom = isLast ? 4 : 1.5;

      const detail = details[key];
      const joinDetail = (
        <GroupOrderJoinDetail
          Icon={detail.icon}
          text={detail.text}
          marginBottom={marginBottom}
        />
      );
      joinDetails.push(joinDetail);
    });

    return joinDetails;
  };

  const handleSubmit = async event => {
    event.preventDefault();

    setIsSubmittingForm(true);

    const result = await dispatch(joinGroupOrder(slug, { name: nameInput }));

    setIsSubmittingForm(false);

    if (result?.error !== true) {
      dispatch(goToMenu());
    }
  };

  return (
    <>
      <ModalCloseButton handleClick={handleClose} />
      <form onSubmit={handleSubmit}>
        <StyledGroupOrderWrapper>
          <TitleTypography>{getGroupJoinTitle(adminName)}</TitleTypography>
          <HowItWorks bottomMargin={'16px'} />
          {renderJoinDetails()}
          <Box mt="16px" mb="35px">
            <StyledInputName
              variant="outlined"
              placeholder="Your Name"
              label=""
              onChange={handleNameChange}
              value={nameInput}
              inputProps={{
                maxLength: GROUP_ORDER_DINER_NAME_INPUT_MAX_LENGTH
              }}
              autoFocus
            />
          </Box>
          <GroupOrderErrorToast />
          <StyledGroupOrderButton
            disabled={isCTAButtonDisabled || isSubmittingForm}
            handleClick={() => {}}
            type="submit"
            fullWidth
            label={getGroupJoinCTALabel(adminName)}
            isMobile={isMobile}
          />
        </StyledGroupOrderWrapper>
      </form>
    </>
  );
};

GroupOrderJoin.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default GroupOrderJoin;
